import React from 'react';
import PropTypes from 'prop-types';
import {get} from 'lodash';
import className from 'classnames';
import axios from 'axios';
import moment from 'moment';
import pluralize from 'pluralize';

import {
  availableBanTimes,
  addNotification,
  CSRF_TOKEN,
  _setUntil,
  noteDisabled
} from '../lib/helpers';

import {
  canBan,
} from '../lib/policies';

import Modal from './Modal';
import Notification from './Notification';
import DateSelector from './DateSelector';

class UserNoteEditModal extends React.Component {
  static propTypes = {
    mode: PropTypes.oneOf([null, 'bulk']),
    uuid: PropTypes.string,
  };

  static defaultProps = {
    onToggleModal: () => {},
    endPoints: {
      penalty: 'api/penalties',
      note: 'api/notes',
    },
  };

  $form = React.createRef()
  $submitButton = React.createRef()

  state = {
    banUntil: _setUntil(this.props.banUntil),
    isLoading: false,
    isBanning: this.props.isBanning ? this.props.isBanning : false,
    banWarning: null,
  }

  componentDidUpdate = async (prevProps, prevState) => {
    if (this.props.isActive && !prevProps.isActive) {
      document.addEventListener('keydown', this.onKeydown);

      let _banWarning = prevState.banWarning

      if (this.props.uuid !== undefined && _banWarning === null) {
        const res = await axios.get(`/${gon.currentProvider}/api/users`, {
          params: {
            uuid: this.props.uuid,
          },
        })

        if (res.status == 200 && get(res, 'data')) {
          _banWarning = get(res, 'data.users').some(({banWarning}) => banWarning === true)
        }
      }

      this.setState({
        banTime: this.props.banTime,
        isBanning: this.props.isBanning,
        banWarning: _banWarning,
      })
    }

    if (!this.props.isActive && prevProps.isActive) {
      document.removeEventListener('keydown', this.onKeydown);

      if (this.$form.current) {
        this.$form.current.reset()
      }

      // E.g. when a user changes time then closes the modal.
      this.setState({
        banUntil: _setUntil(this.props.banUntil),
      })
    }
  }

  componentWillUnmount = () => {
    document.removeEventListener('keydown', this.onKeydown);
  }

  _formatBanTime = (dateTime) => {
    if (this.state.banTime !== 'specific') {
      return this.state.banTime
    }

    const banStart = moment()
    const banEnd = moment(dateTime)

    return moment.duration(banEnd.diff(banStart)).asSeconds()
  }

  _createPenalty = ($els) => {
    this.setState({
      isLoading: true,
    }, () => {
      let penalty = {
        ban_time: this._formatBanTime(this.state.banUntil),
        reasons: this._getCheckedValues($els.reasons),
        note: $els.note !== undefined ? $els.note.value : '',
      }

      if (get(this.props, 'mode') == 'bulk') {
        penalty = {
          ...penalty,
          user_ids: get(this.props, 'userIds'),
        }
      }
      else {
        penalty = {
          ...penalty,
          user_id: get(this.props, 'userId'),
          type: get(this.props, 'type') || 'User',
          post_id: get(this.props, 'postId'),
        }
      }

      axios.post(`/${gon.currentProvider}/${get(this.props, 'endPoints.penalty')}`, {
        penalty,
        authenticity_token: CSRF_TOKEN,
      })
        .then((res) => {
          if (res.status == 200 && get(res, 'data.message')) {
            addNotification({
              message: get(res, 'data.message'),
              type: Notification.NOTIFICATION_TYPES.success,
            })
          }
        })
        .catch((error) => {
          addNotification({
            message: get(error, 'response.data.message'),
            type: Notification.NOTIFICATION_TYPES.error,
          })
        })
        .then(() => {
          // We need to delay this because it's on a worker.
          // So it will not update immediately.
          window.setTimeout(() => {
            window.location.reload()
          }, 3000)
        })
    })
  };

  _createNote = ($els) => {
    let note = {
      note: $els.note.value,
    }

    if (get(this.props, 'mode') == 'bulk') {
      note = {
        ...note,
        user_ids: get(this.props, 'userIds'),
      }
    }
    else {
      note = {
        ...note,
        user_id: get(this.props, 'userId'),
        ban_time: this.state.banTime,
      }
    }

    axios.post(`/${gon.currentProvider}/${get(this.props, 'endPoints.note')}`, {
      note,
      authenticity_token: CSRF_TOKEN,
    })
      .then((res) => {
        if (res.status == 200 && get(res, 'data.message')) {
          addNotification({
            message: get(res, 'data.message'),
            type: Notification.NOTIFICATION_TYPES.success,
          })
        }
      })
      .catch((error) => {
        addNotification({
          message: get(error, 'response.data.message'),
          type: Notification.NOTIFICATION_TYPES.error,
        })
      })
      .then(() => {window.location.reload()})
  };

  _getCheckedValues = (checkboxes) => {
    checkboxes = Array.prototype.slice.call(checkboxes)
    return checkboxes.filter(checkbox => checkbox.checked).map(checkbox => checkbox.value)
  };

  onBanTimeChange = (banTime) => () => {
    this.setState({
      banTime,
    })
  }

  onKeydown = (e) => {
    if (e.target == get(this.$form, 'current.elements.note')) {
      return
    }

    switch (event.key.toLowerCase()) {
      case 'escape': return this.props.onToggleModal()
      case 'enter':
        if (this.$submitButton.current) this.$submitButton.current.click()
        return
    }
  }

  onSubmit = (e) => {
    e.preventDefault()
    const $els = e.target.elements

    if (this.state.isBanning) {
      this._createPenalty($els)
    }
    else {
      this._createNote($els)
    }
  };

  render = () => {
    return (
      <Modal
        isActive={this.props.isActive}
        onToggleModal={this.props.onToggleModal}
      >
        <form
          onSubmit={this.onSubmit}
          ref={this.$form}
        >
          <h3 className='Modal__h3'>
            {this.props.mode == 'bulk' ?
              this.state.isBanning ?
                'Note reason for baning users'
              :
                'Writing note for users'
            :
              this.state.isBanning ?
                `Note reason for baning user ${get(this.props, 'userId')}`
              :
                `Writing note for user ${get(this.props, 'userId')}`
            }
          </h3>

          <div className='UserNoteEditModal'>
            {this.state.isBanning &&
              <React.Fragment>
                <div className='UserNoteEditModal__box'>
                  {(() => {
                    const data = [
                      'Sexually Inappropriate', 'Offensive', 'Violence',
                      'Prohibited Content', 'Spam', 'False News', 'Misleading or Scam',
                    ]

                    return (
                      <ul className='UserNoteEditModal__ul'>
                        {data.map((x, i) => (
                          <li key={i}>
                            <label>
                              <input type='checkbox' name='reasons' value={x.split(' ').join('_').toLowerCase()} />
                              <span
                                className={className('CheckBox', {
                                  'CheckBox--active': false,
                                })}
                              />

                              <span>{x}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    )
                  })()}
                </div>

                <p className='UserNoteEditModal__p'>and</p>
              </React.Fragment>
            }

            {!noteDisabled() &&
              <textarea
                className='Input'
                rows={5}
                placeholder='Write description …'
                name='note'
              />
            }

            <div className='UserNoteEditModal__grid'>
              {!this.props.isBanningEdit &&
                <div className='UserNoteEditModal__grid__row'>
                  <div className='UserNoteEditModal__grid__col'>
                    Ban tools

                    {!noteDisabled() &&
                      <label
                        className={className('Switch', {
                          'Switch--small': true,
                        })}
                      >
                        <div className='Switch__wrapper-outer'>
                          <input
                            type='checkbox'
                            checked={this.state.isBanning ? true : false}
                            onChange={() => {
                              this.setState((prevState) => ({
                                isBanning: !prevState.isBanning,
                              }))
                            }}
                          />
                          <div className='Switch__wrapper' />
                        </div>
                      </label>
                    }
                  </div>

                  {this.state.isBanning &&
                    <div className='UserNoteEditModal__grid__col'>
                      <div className='TableChecker__left__ban-choices'>
                        {availableBanTimes.map((x, i) => (
                          <a
                            key={i}
                            className={className('Badge', {
                              'Badge--icon-lock': true,
                              'Badge--gray': true,
                              'Badge--orange': x.key == this.state.banTime,
                            })}
                            onClick={this.onBanTimeChange(x.key)}
                          >
                            {x.value}
                          </a>
                        ))}

                        <a
                          className={className('Badge', {
                            'Badge--wide': true,
                            'Badge--icon-no-entry': true,
                            'Badge--gray': true,
                            'Badge--orange': this.state.banTime == 'shadow_ban',
                          })}
                          onClick={this.onBanTimeChange('shadow_ban')}
                        >
                          Shadow Ban for 30 Days
                        </a>

                        {canBan('User') &&
                          <a
                            className={className('Badge', {
                              'Badge--wide': true,
                              'Badge--icon-no-entry': true,
                              'Badge--gray': true,
                              'Badge--red': this.state.banTime == 'permanent',
                            })}
                            onClick={this.onBanTimeChange('permanent')}
                          >
                            Ban
                          </a>
                        }
                      </div>
                    </div>
                  }
                </div>
              }

              {(
                this.state.isBanning &&
                (
                  this.state.banTime == 'specific' ||
                  this.props.isBanningEdit
                )
              ) &&
                <div className='UserNoteEditModal__grid__row'>
                  <div
                    className='UserNoteEditModal__grid__col'
                    style={{
                      alignSelf: 'center',
                    }}
                  >
                    Until
                  </div>

                  <div className='UserNoteEditModal__grid__col'>
                    {this.props.isActive &&  // We'll hard reset the DateSelector every time `isActive` changes, I.E. when a user changes datetime then closes the modal.
                      <DateSelector
                        selectedDateTime={this.state.banUntil}
                        onChange={(banUntil) => {
                          this.setState({
                            banUntil,
                          })
                        }}
                      />
                    }
                  </div>
                </div>
              }

              {this.state.isBanning && get(this.props, 'bannedTimeLeft') !== undefined &&
                <div className='UserNoteEditModal__grid__row'>
                  <div className='UserNoteEditModal__grid__col'>
                    Time left
                  </div>

                  <div className='UserNoteEditModal__grid__col UserNoteEditModal__grid__col--red'>
                    {(() => {
                      let text = [get(this.props, 'bannedTimeLeft')]
                      const duration = moment.duration(
                        moment(this.state.banUntil)
                          .diff(moment(this.props.banUntil))
                      )

                      if (duration.days() || duration.hours() || duration.minutes()) {
                        text = [...text, ' + ']
                      }

                      if (duration.days()) {
                        text = [...text, `${duration.days()} ${pluralize('day', duration.days())}`]
                      }
                      if (duration.hours()) {
                        text = [...text, `${duration.hours()} ${pluralize('hour', duration.hours())}`]
                      }
                      if (duration.minutes()) {
                        text = [...text, `${duration.minutes()} ${pluralize('minute', duration.minutes())}`]
                      }

                      return (
                        <React.Fragment>
                          {text.join(' ')}
                        </React.Fragment>
                      )
                    })()}
                  </div>
                </div>
              }
            </div>
          </div>

          <div className='Modal__footer'>
            <a
              className='Button Button--gray-light'
              onClick={this.props.onToggleModal}
            >
              Cancel
            </a>

            <button
              className='Button Button--blue'
              disabled={this.state.isLoading}
              ref={this.$submitButton}
            >
              {this.state.isLoading ? 'Loading …' : 'Submit'}
            </button>
          </div>

          {this.state.isBanning && this.state.banWarning &&
            <div className='Modal__footer--without-border'>
              <p className="Modal__footer__p Modal__footer__p--red">
                Warning: This account or account with the same UUID is an admin account or have high followers
              </p>
            </div>
          }
        </form>
      </Modal>
    );
  }
}

export default UserNoteEditModal;
